import * as turf from '@turf/turf';
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'framer-motion';
import { GoogleMap, Polygon, Marker } from '@react-google-maps/api';
import { Slider, Tooltip } from 'antd';
import { faCheck, faCompressAlt, faFloppyDisk, faRulerHorizontal, faWandMagicSparkles, faXmark, faFileUpload } from '@fortawesome/free-solid-svg-icons';

//STYLES
import { CardFlutuante, CardFlutuanteOpcoes, ContainerModal, OverlayModal, WrapperMapa } from './styled';

//APP
import { errorAlert, successAlert, warningAlert } from '../../../../../../../../../../../../../../utils/alert';

//COMPONENTS
import ButtonCustom from '../../../../../../../../../../../../../../Components/ButtonCustom';
import { Render } from '../../../../../../../../../../../../../../Components/CustomUi/render';
import { TitleH3 } from '../../../../../../../../../../../../../../Components/CustomUi/titleH3';
import { Switcher } from '../../../../../../../../../../../../../../Components/CustomUi/Switcher';
import { Box } from '../../../../../../../../../../../../../../Components/CustomUi/box';
import { Inline } from '../../../../../../../../../../../../../../Components/CustomUi/inline';
import { Warning } from '../../../../../../../../../../../../../../Components/CustomUi/warning';
import { ModalPopOver } from '../../../../../../../../../../../../../../Components/CustomUi/modalPopOver';

// --- Tipagem ---
export interface Coordenada {
    lat: number;
    lng: number;
}

// Nova interface plana conforme solicitado
export interface PointCustom {
    INDICE: number;
    NOME: number | string;
    LATITUDE: number;
    LONGITUDE: number;
    COR?: string | null;
    id?: string | number;
    [key: string]: any;
}

interface PropsModalEditarContorno {
    aberto: boolean;
    contornoInicial?: Coordenada[];
    pontos: PointCustom[] | any; // Atualizado para receber PointCustom
    aoFechar: () => void;
    aoSalvar: (coords: Coordenada[]) => void;
}

export interface PreviaReducaoTrilhas {
    total: number
    aposReducao: number
    removidos: number
    porcentagem: number
}

export interface CorrecaoSobreposicao {
    /** Contorno corrigido — igual ao original quando não foi possível corrigir */
    coords: Coordenada[]
    /** Se a correção zerou as sobreposições */
    resolvido: boolean
    kinksAntes: number
    kinksDepois: number
    /** Vértices com coordenadas iguais existentes antes da correção */
    duplicadosAntes: number
    /** Quantos vértices repetidos a correção removeu (etapa 1) */
    duplicadosRemovidos: number
    /**
     * Se a etapa 2 (unkinkPolygon) precisou rodar. Só nesse caso o formato do
     * contorno muda — remover vértices repetidos não move nenhum vértice.
     */
    formatoAlterado: boolean
    areaAntes: number
    areaDepois: number
    /** Variação de área em % — o número que indica o quanto o contorno mudou */
    variacaoArea: number
    /** Área descartada ao manter apenas a maior parte, em % do total */
    descartado: number
    /** Correção possível, mas fora do limite seguro de variação de área */
    arriscado: boolean
}

// --- Helpers ---

// Casas decimais usadas para considerar dois vértices na "mesma coordenada" (~1cm)
const PRECISAO_COORDENADA = 7

// Um polígono válido precisa de no mínimo 3 vértices distintos
const MINIMO_VERTICES_POLIGONO = 3

// Acima desta variação de área a correção é considerada arriscada e não é aplicada
const LIMITE_VARIACAO_AREA = 5

// Teto de passadas da correção — trava de segurança, o loop para sozinho ao zerar
const MAX_PASSOS_CORRECAO = 10

// Espelha a largura de `CardFlutuanteOpcoes` no styled.ts — usada para abrir os
// popovers ao lado do card sem sobrepô-lo
const LARGURA_CARD_OPCOES = 250

// Respiro entre o popover e o card de opções
const FOLGA_POPOVER = 14

const calcularLimites = (coords: Coordenada[]) => {
    const limites = new google.maps.LatLngBounds();
    coords.forEach((coord) => limites.extend(coord));
    return limites;
};

/** Chave de comparação de coordenada — arredonda para evitar ruído de ponto flutuante */
const chaveDeCoordenada = (lat: number, lng: number) =>
    `${lat.toFixed(PRECISAO_COORDENADA)}_${lng.toFixed(PRECISAO_COORDENADA)}`

/**
 * O contorno pode chegar fechado (último ponto repete o primeiro) ou aberto,
 * dependendo de ter vindo de KML ou da edição manual. Tratamos os dois casos.
 */
const anelFechado = (coords: Coordenada[]) => {
    if (coords.length < 2) return false
    const primeiro = coords[0]
    const ultimo = coords[coords.length - 1]
    return chaveDeCoordenada(primeiro.lat, primeiro.lng) === chaveDeCoordenada(ultimo.lat, ultimo.lng)
}

/** Conta vértices desconsiderando o ponto que apenas fecha o anel */
const contarVerticesReais = (coords: Coordenada[]) => {
    if (coords.length < 2) return coords.length
    return anelFechado(coords) ? coords.length - 1 : coords.length
}

/** Quantos vértices do anel estão exatamente nas mesmas coordenadas */
const contarDuplicadosDoAnel = (coords: Coordenada[]) => {
    const lista = anelFechado(coords) ? coords.slice(0, -1) : coords
    if (lista.length <= MINIMO_VERTICES_POLIGONO) return 0

    const vistos = new Set<string>()
    let duplicados = 0

    lista.forEach(({ lat, lng }) => {
        const chave = chaveDeCoordenada(lat, lng)
        if (vistos.has(chave)) duplicados += 1
        else vistos.add(chave)
    })

    return duplicados
}

/** Remove vértices que estão exatamente nas mesmas coordenadas */
const removerDuplicadosDoAnel = (coords: Coordenada[]) => {
    const fechado = anelFechado(coords)
    const lista = fechado ? coords.slice(0, -1) : coords

    const vistos = new Set<string>()
    const unicos: Coordenada[] = []

    lista.forEach(({ lat, lng }) => {
        const chave = chaveDeCoordenada(lat, lng)
        if (!vistos.has(chave)) {
            vistos.add(chave)
            unicos.push({ lat, lng })
        }
    })

    const removidos = lista.length - unicos.length
    if (unicos.length < MINIMO_VERTICES_POLIGONO) return { coords, removidos: 0 }

    return {
        coords: fechado ? [...unicos, { ...unicos[0] }] : unicos,
        removidos,
    }
}

/** Converte o anel em um polígono turf válido para medição */
const paraPoligonoTurf = (coords: Coordenada[]) => {
    const anel = coords.map(({ lat, lng }) => [lng, lat])
    if (!anelFechado(coords)) anel.push([coords[0].lng, coords[0].lat])
    return turf.polygon([anel])
}

/** Quantidade de auto-interseções (vértices sobrepostos) do contorno */
const contarKinks = (coords: Coordenada[]) => {
    try {
        if (coords.length < MINIMO_VERTICES_POLIGONO) return 0
        return turf.kinks(paraPoligonoTurf(coords)).features.length
    } catch {
        return 0
    }
}

/** Pontos onde o contorno se cruza — usados para marcar as sobreposições no mapa */
const listarPontosDeSobreposicao = (coords: Coordenada[]): Coordenada[] => {
    try {
        if (coords.length < MINIMO_VERTICES_POLIGONO) return []
        return turf.kinks(paraPoligonoTurf(coords)).features.map((f: any) => ({
            lat: f.geometry.coordinates[1],
            lng: f.geometry.coordinates[0],
        }))
    } catch {
        return []
    }
}

/** Área do contorno em hectares */
const areaEmHectares = (coords: Coordenada[]) => {
    try {
        if (coords.length < MINIMO_VERTICES_POLIGONO) return 0
        return turf.area(paraPoligonoTurf(coords)) / 10000
    } catch {
        return 0
    }
}

/**
 * Corrige o contorno com o mínimo de alteração possível.
 *
 * Estratégia em cascata, do menos ao mais invasivo:
 *   1. remover vértices com coordenadas iguais — é a causa mais comum, já que o
 *      contorno é registrado por trator e uma máquina parada grava a mesma
 *      coordenada várias vezes. Não move nenhum vértice e não altera a área;
 *   2. turf.unkinkPolygon, só se ainda sobrar sobreposição — divide o anel no
 *      ponto de cruzamento e mantém a maior parte. Preserva as coordenadas
 *      exatas (não desloca vértices nem infla a contagem).
 *
 * A função é pura: serve para a prévia e para a aplicação.
 */
const corrigirSobreposicoes = (coords: Coordenada[]): CorrecaoSobreposicao => {
    const kinksAntes = contarKinks(coords)
    const areaAntes = areaEmHectares(coords)
    const duplicadosAntes = contarDuplicadosDoAnel(coords)

    const semCorrecao: CorrecaoSobreposicao = {
        coords,
        resolvido: kinksAntes === 0,
        kinksAntes,
        kinksDepois: kinksAntes,
        duplicadosAntes,
        duplicadosRemovidos: 0,
        formatoAlterado: false,
        areaAntes,
        areaDepois: areaAntes,
        variacaoArea: 0,
        descartado: 0,
        arriscado: false,
    }

    // Nada a fazer: sem sobreposição e sem vértices repetidos
    if (kinksAntes === 0 && duplicadosAntes === 0) return semCorrecao
    if (coords.length <= MINIMO_VERTICES_POLIGONO) return semCorrecao

    const montarResultado = (
        bruto: Coordenada[],
        descartado: number,
        duplicadosRemovidos: number,
        formatoAlterado: boolean
    ): CorrecaoSobreposicao => {
        const areaDepois = areaEmHectares(bruto)
        const variacaoArea = areaAntes > 0 ? (Math.abs(areaDepois - areaAntes) / areaAntes) * 100 : 0
        const kinksDepois = contarKinks(bruto)

        return {
            coords: bruto,
            resolvido: kinksDepois === 0,
            kinksAntes,
            kinksDepois,
            duplicadosAntes,
            duplicadosRemovidos,
            formatoAlterado,
            areaAntes,
            areaDepois,
            variacaoArea,
            descartado,
            arriscado: variacaoArea > LIMITE_VARIACAO_AREA || descartado > LIMITE_VARIACAO_AREA,
        }
    }

    // 1) Sempre limpa os vértices repetidos primeiro — passo mais barato e sem risco
    const semDuplicados = removerDuplicadosDoAnel(coords)
    let atual = semDuplicados.removidos > 0 ? semDuplicados.coords : coords
    const duplicadosRemovidos = semDuplicados.removidos

    // Resolveu só com isso: é o caso mais comum (trator parado repetindo coordenada).
    // O formato do contorno não muda, então não há o que revisar.
    if (contarKinks(atual) === 0) {
        if (duplicadosRemovidos === 0) return semCorrecao
        return montarResultado(atual, 0, duplicadosRemovidos, false)
    }

    /**
     * 2) Divide no ponto de cruzamento e mantém a maior parte.
     *
     * Em loop: um contorno com várias sobreposições pode não ser resolvido em uma
     * única passada, e parar no meio deixaria o alerta aceso mesmo "após a correção".
     * Só seguimos enquanto o número de kinks realmente diminui.
     */
    let descartadoAcumulado = 0
    let rodouUnkink = false

    for (let passo = 0; passo < MAX_PASSOS_CORRECAO; passo++) {
        const kinksAtuais = contarKinks(atual)
        if (kinksAtuais === 0) break

        try {
            const partes = turf.unkinkPolygon(paraPoligonoTurf(atual))
            if (!partes?.features?.length) break

            const areas = partes.features.map((feature: any) => turf.area(feature))
            const areaTotal = areas.reduce((soma: number, valor: number) => soma + valor, 0)
            const indiceMaior = areas.indexOf(Math.max(...areas))

            const maior = partes.features[indiceMaior]
            const resultado = maior.geometry.coordinates[0].map(([lng, lat]: number[]) => ({ lat, lng }))
            if (contarVerticesReais(resultado) < MINIMO_VERTICES_POLIGONO) break

            // Sem progresso: aborta para não girar em falso nem degradar o contorno
            if (contarKinks(resultado) >= kinksAtuais) break

            if (areaTotal > 0) {
                descartadoAcumulado += ((areaTotal - areas[indiceMaior]) / areaTotal) * 100
            }
            atual = resultado
            rodouUnkink = true
        } catch {
            break
        }
    }

    // Nem os duplicados nem o unkink alteraram o contorno
    if (atual === coords) return semCorrecao

    return montarResultado(atual, descartadoAcumulado, duplicadosRemovidos, rodouUnkink)
}

/**
 * Reduz a quantidade de vértices mantendo apenas os que estão a pelo menos
 * `distanciaMinima` metros do último vértice preservado. Função pura: serve
 * para a prévia e para a aplicação.
 */
const calcularReducaoDeTrilhas = (coords: Coordenada[], distanciaMinima: number) => {
    if (coords.length <= MINIMO_VERTICES_POLIGONO) return coords

    // O fechamento do anel é tratado à parte para nunca ser descartado
    const fechado = anelFechado(coords)
    const lista = fechado ? coords.slice(0, -1) : coords

    if (lista.length <= MINIMO_VERTICES_POLIGONO) return coords

    const mantidos: Coordenada[] = [lista[0]]

    for (let i = 1; i < lista.length; i++) {
        const atual = lista[i]
        const referencia = mantidos[mantidos.length - 1]

        const distancia = turf.distance(
            turf.point([referencia.lng, referencia.lat]),
            turf.point([atual.lng, atual.lat]),
            { units: 'meters' }
        )

        if (distancia >= distanciaMinima) {
            mantidos.push(atual)
        }
    }

    // Garante que o polígono continue válido mesmo com um intervalo agressivo
    if (mantidos.length < MINIMO_VERTICES_POLIGONO) return coords

    // O último vértice mantido pode ter ficado colado no primeiro ao fechar o anel
    if (mantidos.length > MINIMO_VERTICES_POLIGONO) {
        const inicio = mantidos[0]
        const fim = mantidos[mantidos.length - 1]
        const distanciaFechamento = turf.distance(
            turf.point([inicio.lng, inicio.lat]),
            turf.point([fim.lng, fim.lat]),
            { units: 'meters' }
        )
        if (distanciaFechamento < distanciaMinima) {
            mantidos.pop()
        }
    }

    return fechado ? [...mantidos, { ...mantidos[0] }] : mantidos
}

/**
 * Simplifica apenas trechos retos do contorno (Douglas-Peucker).
 *
 * Diferente da redução por distância, aqui o critério é o desvio perpendicular:
 * um vértice só é removido se a linha continuar praticamente no mesmo lugar sem
 * ele. Assim uma reta com 50 vértices colapsa para 2, enquanto cantos e curvas
 * são preservados.
 */
const calcularReducaoDeRetas = (coords: Coordenada[], toleranciaMetros: number) => {
    if (coords.length <= MINIMO_VERTICES_POLIGONO) return coords

    const primeiro = coords[0]
    const fechado = anelFechado(coords)

    // turf.simplify espera um anel fechado
    const anel = fechado ? coords : [...coords, { ...primeiro }]
    if (anel.length < 4) return coords

    // A tolerância do turf.simplify é em graus — convertemos a partir dos metros
    const grausPorMetro = 1 / 111320
    const tolerancia = toleranciaMetros * grausPorMetro

    try {
        /**
         * Simplificamos como LineString, e não como Polygon: turf.simplify lança
         * "invalid polygon" quando o anel cairia abaixo de 4 coordenadas, e não
         * simplifica anéis auto-interseccionados — justamente o caso dos
         * contornos que este modal precisa corrigir.
         */
        const linha = turf.lineString(anel.map(({ lat, lng }) => [lng, lat]))
        const simplificada = turf.simplify(linha, { tolerance: tolerancia, highQuality: true })

        const resultado: Coordenada[] = simplificada.geometry.coordinates.map(([lng, lat]: number[]) => ({ lat, lng }))

        // Se a simplificação degenerar o polígono, mantém o original
        if (contarVerticesReais(resultado) < MINIMO_VERTICES_POLIGONO) return coords

        // Garante que o anel continue fechado após a simplificação
        if (!anelFechado(resultado)) resultado.push({ ...resultado[0] })

        return fechado ? resultado : resultado.slice(0, -1)
    } catch {
        return coords
    }
}

/** Legenda das cores desenhadas no mapa durante a prévia */
const LegendaDaPreviaNoMapa: React.FC<{ rotuloVertices?: string }> = ({
    rotuloVertices = 'Vértices que serão removidos',
}) => (
    <Box gap={6}>
        <label style={{ fontSize: 11, color: '#7b7b7b', fontWeight: 600 }}>
            Prévia no mapa
        </label>
        <Inline alignItems='center' gap={8}>
            <span style={{ width: 18, height: 3, background: '#00e676', borderRadius: 2 }} />
            <label style={{ fontSize: 11, color: '#7b7b7b' }}>Contorno resultante</label>
        </Inline>
        <Inline alignItems='center' gap={8}>
            <span style={{ width: 10, height: 10, background: '#ff9100', borderRadius: '50%' }} />
            <label style={{ fontSize: 11, color: '#7b7b7b' }}>{rotuloVertices}</label>
        </Inline>
    </Box>
)

/** Números da prévia + legenda das cores desenhadas no mapa */
const ResumoDaPrevia: React.FC<{ previa: PreviaReducaoTrilhas }> = ({ previa }) => (
    <>
        <Box gap={8}>
            <Inline justifyContent='space-between'>
                <label style={{ fontSize: 12, color: '#7b7b7b' }}>Total de vértices:</label>
                <strong style={{ fontSize: 13, color: '#505050' }}>{previa.total}</strong>
            </Inline>

            <Inline justifyContent='space-between'>
                <label style={{ fontSize: 12, color: '#7b7b7b' }}>Vértices após a redução:</label>
                <strong style={{ fontSize: 13, color: '#2b8a3e' }}>{previa.aposReducao}</strong>
            </Inline>

            <Inline justifyContent='space-between'>
                <label style={{ fontSize: 12, color: '#7b7b7b' }}>Porcentagem de redução:</label>
                <strong style={{ fontSize: 13, color: '#c94a4a' }}>
                    {previa.porcentagem.toFixed(1)}%
                </strong>
            </Inline>
        </Box>

        <LegendaDaPreviaNoMapa />
    </>
)

/** Prévia vazia — evita recriar o objeto em cada retorno antecipado */
const PREVIA_VAZIA: PreviaReducaoTrilhas = { total: 0, aposReducao: 0, removidos: 0, porcentagem: 0 }

/** Calcula a prévia comparando o contorno atual com o resultado da redução */
const montarPrevia = (coords: Coordenada[], reduzido: Coordenada[]): PreviaReducaoTrilhas => {
    const total = contarVerticesReais(coords)
    if (total === 0) return PREVIA_VAZIA

    const aposReducao = contarVerticesReais(reduzido)
    const removidos = total - aposReducao

    return { total, aposReducao, removidos, porcentagem: (removidos / total) * 100 }
}

// --- Componente ---

const OverlayMotion = motion(OverlayModal);
const ContainerMotion = motion(ContainerModal);

export const EditarContorno: React.FC<PropsModalEditarContorno> = ({
    aberto,
    contornoInicial = [],
    pontos,
    aoFechar,
    aoSalvar,
}) => {
    const contornoSeguro = contornoInicial || [];

    // --- Estados ---

    const [historico, setHistorico] = useState<Coordenada[][]>([contornoSeguro]);
    const [indiceHistorico, setIndiceHistorico] = useState(0);

    // Atualizado para PointCustom
    const [points, setPoints] = useState<PointCustom[]>([]);
    const [pontosFora, setPontosFora] = useState<PointCustom[]>([]);

    const [exibir_pontos, setExibir_pontos] = useState<boolean>(false)
    const [contornoValido, setContornoValido] = useState<boolean>(true)

    // Ferramentas de limpeza do contorno
    const [openCorrigirKinks, setOpenCorrigirKinks] = useState<boolean>(false)
    const [openReducaoTrilhas, setOpenReducaoTrilhas] = useState<boolean>(false)
    const [distanciaTrilhas, setDistanciaTrilhas] = useState<number>(1)
    const [openReducaoRetas, setOpenReducaoRetas] = useState<boolean>(false)
    const [toleranciaRetas, setToleranciaRetas] = useState<number>(1)
    const [larguraJanela, setLarguraJanela] = useState<number>(() => window.innerWidth)

    const refPoligono = useRef<google.maps.Polygon | null>(null);
    const refMapa = useRef<google.maps.Map | null>(null);
    const refInputFile = useRef<HTMLInputElement | null>(null);

    const caminhoAtual = historico[indiceHistorico] || [];

    // --- Lógica de Histórico ---

    const adicionarAoHistorico = (novoCaminho: Coordenada[]) => {
        const novoHistorico = historico.slice(0, indiceHistorico + 1);
        novoHistorico.push(novoCaminho);
        setHistorico(novoHistorico);
        setIndiceHistorico(novoHistorico.length - 1);
    };

    // const desfazer = () => {
    //   if (indiceHistorico > 0) {
    //     setIndiceHistorico(indiceHistorico - 1);
    //   }
    // };

    // const refazer = () => {
    //   if (indiceHistorico < historico.length - 1) {
    //     setIndiceHistorico(indiceHistorico + 1);
    //   }
    // };

    const resetar = () => {
        setHistorico([contornoSeguro]);
        setIndiceHistorico(0);
        if (refMapa.current && contornoSeguro.length > 0) {
            const limites = calcularLimites(contornoSeguro);
            refMapa.current.fitBounds(limites);
        }
    };

    const salvarEdicao = () => {
        aoSalvar(caminhoAtual);
        aoFechar();
    };


    // --- Ferramentas de limpeza do contorno ---

    /**
     * Os popovers abrem à esquerda do card de opções, que é posicionado em
     * porcentagem (`left: 90%`) e portanto "anda" conforme a largura da janela.
     * Um deslocamento fixo colidiria com o card em telas largas, então o `right`
     * é derivado da mesma conta do CSS: o card começa em 90% da largura menos
     * metade dele, e o popover encosta aí com uma folga.
     */
    const afastamentoPopOver = useMemo(
        () => Math.round(larguraJanela * 0.10 + LARGURA_CARD_OPCOES / 2 + FOLGA_POPOVER),
        [larguraJanela]
    )

    useEffect(() => {
        if (!aberto) return
        const aoRedimensionar = () => setLarguraJanela(window.innerWidth)
        window.addEventListener('resize', aoRedimensionar)
        return () => window.removeEventListener('resize', aoRedimensionar)
    }, [aberto])

    /**
     * Diferente do módulo de grid, aqui o contorno é estado do React
     * (`caminhoAtual`, vindo do histórico) e não o path mutável do Google Maps.
     * Por isso as prévias derivam direto dele — não existe o problema de cache
     * que obrigava a versionar o contorno lá.
     */
    const correcaoSobreposicoes = useMemo(
        () => corrigirSobreposicoes(caminhoAtual),
        [caminhoAtual]
    )

    const temAlgoParaCorrigir =
        correcaoSobreposicoes.kinksAntes > 0 || correcaoSobreposicoes.duplicadosAntes > 0

    /** Pontos de cruzamento do contorno atual — marcados no mapa */
    const pontosSobreposicao = useMemo(
        () => listarPontosDeSobreposicao(caminhoAtual),
        [caminhoAtual]
    )

    const previaReducaoTrilhas = useMemo(
        () => montarPrevia(caminhoAtual, calcularReducaoDeTrilhas(caminhoAtual, distanciaTrilhas)),
        [caminhoAtual, distanciaTrilhas]
    )

    const previaReducaoRetas = useMemo(
        () => montarPrevia(caminhoAtual, calcularReducaoDeRetas(caminhoAtual, toleranciaRetas)),
        [caminhoAtual, toleranciaRetas]
    )

    /**
     * Geometria da prévia desenhada no mapa enquanto um popover está aberto:
     * o contorno resultante e os vértices que serão descartados.
     */
    const previewNoMapa = useMemo(() => {
        const vazio = { contorno: [] as Coordenada[], descartados: [] as Coordenada[] }

        if (!openCorrigirKinks && !openReducaoTrilhas && !openReducaoRetas) return vazio
        if (caminhoAtual.length <= MINIMO_VERTICES_POLIGONO) return vazio

        let reduzido: Coordenada[]
        if (openCorrigirKinks) reduzido = correcaoSobreposicoes.coords
        else if (openReducaoRetas) reduzido = calcularReducaoDeRetas(caminhoAtual, toleranciaRetas)
        else reduzido = calcularReducaoDeTrilhas(caminhoAtual, distanciaTrilhas)

        // Nada a mostrar quando a redução/correção devolveu o contorno intacto
        if (reduzido === caminhoAtual) return vazio
        if (openCorrigirKinks && !temAlgoParaCorrigir) return vazio

        // Vértices que não sobreviveram à redução
        const mantidos = new Set(reduzido.map(({ lat, lng }) => chaveDeCoordenada(lat, lng)))
        const descartados = caminhoAtual.filter(
            ({ lat, lng }) => !mantidos.has(chaveDeCoordenada(lat, lng))
        )

        return { contorno: reduzido, descartados }
    }, [
        caminhoAtual,
        openCorrigirKinks,
        openReducaoTrilhas,
        openReducaoRetas,
        toleranciaRetas,
        distanciaTrilhas,
        correcaoSobreposicoes,
        temAlgoParaCorrigir,
    ])

    /**
     * Aplica um contorno novo entrando no histórico — assim a ferramenta é
     * desfazível como qualquer edição manual de vértice.
     */
    const aplicarCorrecaoSobreposicoes = () => {
        const {
            coords,
            resolvido,
            arriscado,
            variacaoArea,
            duplicadosRemovidos,
            formatoAlterado,
            kinksAntes,
            kinksDepois,
        } = correcaoSobreposicoes

        if (!temAlgoParaCorrigir) {
            warningAlert('Nenhuma sobreposição ou vértice repetido encontrado no contorno.')
            return
        }

        // Correção possível, mas alteraria demais o contorno — decisão fica com o usuário
        if (arriscado) {
            errorAlert(
                `Correção não aplicada: alteraria ${variacaoArea.toFixed(1)}% da área. Ajuste os vértices manualmente.`
            )
            return
        }

        // Nada mudou: nem duplicados removidos, nem o unkink rodou
        if (duplicadosRemovidos === 0 && !formatoAlterado) {
            warningAlert('Não foi possível corrigir automaticamente. Ajuste os vértices manualmente.')
            return
        }

        adicionarAoHistorico(coords)
        setOpenCorrigirKinks(false)

        // Detalha o que cada etapa resolveu — duplicados são a causa mais comum
        const partes: string[] = []
        if (duplicadosRemovidos > 0) {
            partes.push(`${duplicadosRemovidos} vértice(s) repetido(s) removido(s)`)
        }
        // Só creditamos a correção de sobreposição quando a etapa 2 realmente rodou:
        // vértices repetidos por si já acusam kink, e removê-los resolve sem ajustar nada
        if (kinksAntes > 0 && formatoAlterado) {
            partes.push(
                resolvido
                    ? `${kinksAntes} sobreposição(ões) corrigida(s)`
                    : `${kinksAntes - kinksDepois} de ${kinksAntes} sobreposição(ões) corrigida(s)`
            )
        }

        const resumo = partes.join(' e ')

        if (!resolvido) {
            errorAlert(
                `${resumo}. Ainda restam ${kinksDepois} sobreposição(ões) — ajuste os vértices manualmente.`
            )
            return
        }

        successAlert(
            formatoAlterado
                ? `${resumo}. Revise o contorno antes de salvar.`
                : `Correção automática aplicada: ${resumo}.`
        )
    }

    const aplicarReducaoTrilhas = () => {
        if (caminhoAtual.length <= MINIMO_VERTICES_POLIGONO) {
            warningAlert('Não é possível reduzir: o polígono precisa ter no mínimo 3 vértices.')
            return
        }

        const reduzido = calcularReducaoDeTrilhas(caminhoAtual, distanciaTrilhas)
        const removidos = caminhoAtual.length - reduzido.length

        if (removidos <= 0) {
            warningAlert(
                `Nenhum vértice foi removido com o intervalo de ${distanciaTrilhas}m. Tente um valor maior.`
            )
            return
        }

        adicionarAoHistorico(reduzido)
        setOpenReducaoTrilhas(false)
        successAlert(`${removidos} vértice(s) removido(s) pela redução de trilhas.`)
    }

    const aplicarReducaoRetas = () => {
        if (caminhoAtual.length <= MINIMO_VERTICES_POLIGONO) {
            warningAlert('Não é possível simplificar: o polígono precisa ter no mínimo 3 vértices.')
            return
        }

        const reduzido = calcularReducaoDeRetas(caminhoAtual, toleranciaRetas)
        const removidos = caminhoAtual.length - reduzido.length

        if (removidos <= 0) {
            warningAlert(
                `Nenhum trecho reto foi encontrado com a tolerância de ${toleranciaRetas}m. Tente um valor maior.`
            )
            return
        }

        adicionarAoHistorico(reduzido)
        setOpenReducaoRetas(false)
        successAlert(`${removidos} vértice(s) removido(s) de trechos retos.`)
    }

    // Fechar o modal não deve deixar popover aberto para a próxima abertura
    useEffect(() => {
        if (aberto) return
        setOpenCorrigirKinks(false)
        setOpenReducaoTrilhas(false)
        setOpenReducaoRetas(false)
    }, [aberto])

    // --- Verificação de Pontos Fora do Polígono (Atualizado para Flat Object) ---

    useEffect(() => {
        if (!window.google || !window.google.maps || !window.google.maps.geometry) return;
        if (caminhoAtual.length < 3 || points.length === 0) {
            setPontosFora([]);
            return;
        }

        const poligonoTemp = new google.maps.Polygon({ paths: caminhoAtual });
        const fora: PointCustom[] = [];

        points.forEach((pt) => {
            // AJUSTE: Leitura direta de LATITUDE e LONGITUDE
            const lat = Number(pt.LATITUDE);
            const lng = Number(pt.LONGITUDE);

            const latLng = new google.maps.LatLng(lat, lng);

            const estaDentro = google.maps.geometry.poly.containsLocation(latLng, poligonoTemp);

            if (!estaDentro) {
                fora.push(pt);
            }
        });

        setPontosFora(fora);

    }, [caminhoAtual, points]);

    // --- Validação de localização do KML importado ---

    const validarContornoComPontos = (novasCoords: Coordenada[]): boolean => {
        if (!window.google || !window.google.maps || !window.google.maps.geometry) return true;
        if (points.length === 0) return true;

        const poligonoTemp = new google.maps.Polygon({ paths: novasCoords });

        // Verifica se pelo menos um ponto da malha está dentro do novo contorno
        const algumDentro = points.some((pt) => {
            const lat = Number(pt.LATITUDE);
            const lng = Number(pt.LONGITUDE);
            const latLng = new google.maps.LatLng(lat, lng);
            return google.maps.geometry.poly.containsLocation(latLng, poligonoTemp);
        });

        return algumDentro;
    };

    // --- Importação de KML ---

    const dispararInputFile = () => {
        refInputFile.current?.click();
    };

    const lerKML = (e: React.ChangeEvent<HTMLInputElement>) => {
        const arquivo = e.target.files?.[0];
        if (!arquivo) return;

        const reader = new FileReader();
        reader.onload = (evento) => {
            const textoKml = evento.target?.result as string;
            try {
                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString(textoKml, "text/xml");

                const coordsTags = xmlDoc.getElementsByTagName("coordinates");

                if (coordsTags.length > 0) {
                    const coordsRaw = coordsTags[0].textContent?.trim() || "";

                    const novasCoords: Coordenada[] = coordsRaw.split(/\s+/).map((coordStr) => {
                        const [lng, lat] = coordStr.split(",").map(Number);
                        return { lat, lng };
                    }).filter(c => !isNaN(c.lat) && !isNaN(c.lng));

                    if (novasCoords.length > 2) {
                        // Validar se o KML importado contém os pontos da malha
                        const valido = validarContornoComPontos(novasCoords);
                        setContornoValido(valido);

                        if (!valido) {
                            warningAlert('O KML importado não corresponde à localização dos pontos da malha.');
                        }

                        adicionarAoHistorico(novasCoords);

                        if (refMapa.current) {
                            const limites = calcularLimites(novasCoords);
                            refMapa.current.fitBounds(limites);
                        }
                    } else {
                        alert("KML inválido ou sem coordenadas suficientes.");
                    }
                }
            } catch (erro) {
                console.error("Erro ao ler KML", erro);
                alert("Erro ao processar o arquivo KML.");
            }
        };
        reader.readAsText(arquivo);
        e.target.value = "";
    };


    // --- Eventos do Mapa ---

    const aoCarregarMapa = useCallback((map: google.maps.Map) => {
        refMapa.current = map;
        if (contornoSeguro.length > 0) {
            const limites = calcularLimites(contornoSeguro);
            map.fitBounds(limites);
        }
    }, [contornoSeguro]);

    const aoCarregarPoligono = useCallback((poly: google.maps.Polygon) => {
        refPoligono.current = poly;
    }, []);

    const aoEditarPoligono = () => {
        if (refPoligono.current) {
            const path = refPoligono.current.getPath();
            const novasCoords: Coordenada[] = [];

            for (let i = 0; i < path.getLength(); i++) {
                const ponto = path.getAt(i);
                novasCoords.push({ lat: ponto.lat(), lng: ponto.lng() });
            }

            if (JSON.stringify(novasCoords) !== JSON.stringify(caminhoAtual)) {
                adicionarAoHistorico(novasCoords);
            }
        }
    };

    const aoClicarDireito = (e: google.maps.PolyMouseEvent) => {
        if (e.vertex !== undefined && e.vertex !== null) {
            const novoCaminho = [...caminhoAtual];
            novoCaminho.splice(e.vertex, 1);
            adicionarAoHistorico(novoCaminho);
        }
    };

    // --- Inicialização de Pontos (Simplificado para array plano) ---
    useEffect(() => {
        if (pontos) {
            // Se vier como array direto, seta. Se vier dentro de features (legado), extrai.
            if (Array.isArray(pontos)) {
                setPoints(pontos);
            } else if (pontos.features && Array.isArray(pontos.features)) {
                // Caso extremo de compatibilidade, mas idealmente não deve cair aqui com a nova tipagem
                // Se cair, teria que converter, mas vamos assumir array plano
                setPoints(pontos.features);
            }
        }
    }, [pontos]);

    // --- Reset e Init ---
    // Só reinicia o histórico ao ABRIR o modal. `contornoSeguro` não entra nas
    // dependências de propósito: é uma nova referência a cada render do pai, e
    // reagir a ela descartaria a edição em andamento do usuário.
    useEffect(() => {
        if (aberto) {
            setHistorico([contornoInicial || []]);
            setIndiceHistorico(0);
            setContornoValido(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aberto]);

    useEffect(() => {
        if (refMapa.current && caminhoAtual.length > 0) {
            const timer = setTimeout(() => {
                const limites = calcularLimites(caminhoAtual);
                refMapa.current?.fitBounds(limites);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [aberto]);

    return (
        <AnimatePresence>
            {aberto && (
                <OverlayMotion
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <ContainerMotion
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <WrapperMapa>
                            <GoogleMap
                                mapContainerStyle={{ width: '100%', height: '100%' }}
                                zoom={14}
                                onLoad={aoCarregarMapa}
                                options={{
                                    mapTypeId: 'satellite',
                                    streetViewControl: false,
                                    mapTypeControl: false,
                                    fullscreenControl: false,
                                }}
                            >
                                {caminhoAtual && caminhoAtual.length > 0 && (
                                    <Polygon
                                        onLoad={aoCarregarPoligono}
                                        paths={caminhoAtual}
                                        options={{
                                            /**
                                             * Vermelho sinaliza contorno com sobreposição;
                                             * volta ao azul assim que estiver limpo.
                                             */
                                            fillColor: temAlgoParaCorrigir ? '#ff4444' : '#2196F3',
                                            fillOpacity: 0.4,
                                            strokeColor: temAlgoParaCorrigir ? '#ff4444' : '#2196F3',
                                            strokeOpacity: 1,
                                            strokeWeight: 2,
                                            // `editable` é o que desenha os vértices arrastáveis: sem ele o
                                            // polígono fica estático e os handlers abaixo nunca disparam.
                                            editable: true,
                                            // Arrastar o polígono inteiro deslocaria o talhão do lugar; a
                                            // edição aqui é só de vértices.
                                            draggable: false,
                                            zIndex: 1
                                        }}
                                        onMouseUp={aoEditarPoligono}
                                        onDragEnd={aoEditarPoligono}
                                        onRightClick={aoClicarDireito}
                                    />
                                )}

                                {/* Prévia da redução — contorno resultante em verde */}
                                <Render condition={previewNoMapa.contorno.length > 0}>
                                    <Polygon
                                        paths={previewNoMapa.contorno}
                                        options={{
                                            fillOpacity: 0,
                                            strokeColor: '#00e676',
                                            strokeWeight: 3,
                                            clickable: false,
                                            editable: false,
                                            draggable: false,
                                            zIndex: 5,
                                        }}
                                    />
                                </Render>

                                {/* Prévia da redução — vértices que serão removidos */}
                                {previewNoMapa.descartados.map((pt, i) => (
                                    <Marker
                                        key={`descartado_${i}`}
                                        position={pt}
                                        icon={{
                                            path: google.maps.SymbolPath.CIRCLE,
                                            fillColor: '#ff9100',
                                            fillOpacity: 0.9,
                                            strokeColor: '#7a3f00',
                                            strokeWeight: 1,
                                            scale: 4,
                                        }}
                                        clickable={false}
                                        zIndex={6}
                                    />
                                ))}

                                {/*
                                  * Marcadores nos pontos de sobreposição — o pino alto
                                  * aponta para o vértice e não se perde no meio dos
                                  * vértices editáveis do contorno.
                                  */}
                                {pontosSobreposicao.map((pt, i) => (
                                    <Marker
                                        key={`kink_${i}`}
                                        position={pt}
                                        icon={{
                                            path: 'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z',
                                            fillColor: '#ff0000',
                                            fillOpacity: 1,
                                            strokeColor: '#000',
                                            strokeWeight: 1,
                                            scale: 1,
                                            labelOrigin: new window.google.maps.Point(0, -28),
                                        }}
                                        label={{ text: '!', color: 'white', fontWeight: 'semi-bold' }}
                                        zIndex={7}
                                    />
                                ))}

                                <Render condition={exibir_pontos}>
                                    {points?.map((pt, index) => {
                                        // AJUSTE: Leitura direta de props
                                        const lng = Number(pt.LONGITUDE);
                                        const lat = Number(pt.LATITUDE);

                                        // Prioridade para INDICE
                                        const pId = pt.INDICE || pt.id || index;

                                        // Verifica se está fora usando INDICE ou referência
                                        const estaFora = pontosFora.some(pf =>
                                            pf.INDICE === pt.INDICE
                                        );

                                        const corMarcador = estaFora ? '#FF0000' : '#ffffff';

                                        const labelText = pt.NOME
                                            ? pt.NOME.toString()
                                            : (index + 1).toString();

                                        const markerKey = `${pId}-${lat}-${lng}`;

                                        return (
                                            <Marker
                                                key={markerKey}
                                                position={{ lat, lng }}
                                                label={{
                                                    text: labelText,
                                                    color: estaFora ? 'white' : '#7b7b7b',
                                                    fontWeight: 'bold',
                                                    fontSize: '10px',
                                                }}
                                                icon={{
                                                    path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
                                                    fillColor: corMarcador,
                                                    fillOpacity: 1,
                                                    strokeWeight: 1,
                                                    strokeColor: '#2c2c2c',
                                                    scale: 1.5,
                                                    anchor: new google.maps.Point(12, 22),
                                                    labelOrigin: new google.maps.Point(12, 9),
                                                }}
                                            />
                                        )
                                    })}
                                </Render>
                            </GoogleMap>


                            {/* CARD FLUTUANTE */}
                            <CardFlutuanteOpcoes>
                                <TitleH3>Contorno</TitleH3>

                                <Render condition={temAlgoParaCorrigir}>
                                    <Warning style={{ fontSize: 11 }} type='warning'>
                                        Contorno com {correcaoSobreposicoes.kinksAntes} sobreposição(ões) e{' '}
                                        {correcaoSobreposicoes.duplicadosAntes} vértice(s) repetido(s). Os pinos
                                        vermelhos no mapa marcam onde estão os cruzamentos.
                                    </Warning>
                                </Render>

                                <Tooltip placement='left' title={`Corrigir automaticamente linhas sobrepostas e vértices duplicados (${temAlgoParaCorrigir ? `encontrado ${correcaoSobreposicoes.kinksAntes} sobreposições e ${correcaoSobreposicoes.duplicadosAntes} vértices repetidos` : 'Nenhuma sobreposição ou vértice repetido nesse contorno'})`}><span>
                                    <ButtonCustom
                                        onClick={() => {
                                            setOpenReducaoTrilhas(false)
                                            setOpenReducaoRetas(false)
                                            setOpenCorrigirKinks(!openCorrigirKinks)
                                        }}
                                        type={openCorrigirKinks ? 'primary' : 'secondary'}
                                        icon={<FontAwesomeIcon icon={faWandMagicSparkles} />}
                                        disabled={!temAlgoParaCorrigir}
                                        style={{ width: '100%', justifyContent: 'center' }}
                                    >
                                        Corrigir automaticamente
                                    </ButtonCustom>
                                </span></Tooltip>

                                <Tooltip placement='left' title='Reduzir a quantidade de vértices do contorno por distância'><span>
                                    <ButtonCustom
                                        onClick={() => {
                                            setOpenReducaoRetas(false)
                                            setOpenCorrigirKinks(false)
                                            setOpenReducaoTrilhas(!openReducaoTrilhas)
                                        }}
                                        type={openReducaoTrilhas ? 'primary' : 'secondary'}
                                        icon={<FontAwesomeIcon icon={faCompressAlt} />}
                                        style={{ width: '100%', justifyContent: 'center' }}
                                    >
                                        Redutor de trilhas
                                    </ButtonCustom>
                                </span></Tooltip>

                                <Tooltip placement='left' title='Simplificar trechos retos do contorno, preservando cantos e curvas'><span>
                                    <ButtonCustom
                                        onClick={() => {
                                            setOpenReducaoTrilhas(false)
                                            setOpenCorrigirKinks(false)
                                            setOpenReducaoRetas(!openReducaoRetas)
                                        }}
                                        type={openReducaoRetas ? 'primary' : 'secondary'}
                                        icon={<FontAwesomeIcon icon={faRulerHorizontal} />}
                                        style={{ width: '100%', justifyContent: 'center' }}
                                    >
                                        Reduzir trilhas retas
                                    </ButtonCustom>
                                </span></Tooltip>

                                <div style={{ height: 1, background: '#eee' }} />

                                <TitleH3>Pontos foras</TitleH3>
                                <Switcher label='Exibir pontos' isChecked={exibir_pontos} trigger={setExibir_pontos} />
                                <Render condition={exibir_pontos && pontosFora?.length > 0}>
                                    <div className='pontos__grid'>
                                        {pontosFora?.map((item) => (
                                            <div className='pontos__fora'>
                                                {/* AJUSTE: Acesso direto ao NOME */}
                                                {item.NOME}
                                            </div>
                                        ))}
                                    </div>
                                </Render>
                            </CardFlutuanteOpcoes>

                            <CardFlutuante>

                                <input
                                    type="file"
                                    accept=".kml"
                                    ref={refInputFile}
                                    style={{ display: 'none' }}
                                    onChange={lerKML}
                                />
                                {/* 
            <ButtonCustom onClick={desfazer} type='secondary' icon={<FontAwesomeIcon icon={faArrowLeftRotate} />} />
            <ButtonCustom onClick={refazer} type='secondary' icon={<FontAwesomeIcon icon={faArrowRightRotate} />} />
            <ButtonCustom onClick={resetar} type='tertiary' icon={<FontAwesomeIcon icon={faArrowsRotate} />} >Resetar</ButtonCustom> */}

                                <ButtonCustom
                                    onClick={dispararInputFile}
                                    type='tertiary'
                                    icon={<FontAwesomeIcon icon={faFileUpload} />}
                                    title="Importar KML"
                                >
                                    Importar KML
                                </ButtonCustom>

                                <div style={{ width: 1, height: 20, background: '#eee' }} />

                                <ButtonCustom danger icon={<FontAwesomeIcon icon={faXmark} />} onClick={aoFechar}>
                                    Cancelar
                                </ButtonCustom>

                                <ButtonCustom icon={<FontAwesomeIcon icon={faFloppyDisk} />} onClick={salvarEdicao} disabled={pontosFora?.length > 0 || !contornoValido}>
                                    Salvar
                                </ButtonCustom>
                            </CardFlutuante>

                            <ModalPopOver
                                title='Corrigir automaticamente'
                                open={openCorrigirKinks}
                                onClose={() => setOpenCorrigirKinks(false)}
                                width={330}
                                height='auto'
                                top={30}
                                right={afastamentoPopOver}
                            >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                                    {correcaoSobreposicoes.formatoAlterado ? (
                                        <Warning style={{ fontSize: 11 }} type='warning'>
                                            Remover os vértices repetidos não resolveu tudo, então o contorno também será
                                            ajustado. Como o formato muda, revise o contorno e a área antes de salvar.
                                        </Warning>
                                    ) : (
                                        <Warning style={{ fontSize: 11 }} type='info'>
                                            Remove os vértices que estão na mesma coordenada. Não altera o formato nem a área
                                            do contorno.
                                        </Warning>
                                    )}

                                    <Box gap={8}>
                                        <Inline justifyContent='space-between'>
                                            <label style={{ fontSize: 12, color: '#7b7b7b' }}>Vértices repetidos:</label>
                                            <strong
                                                style={{
                                                    fontSize: 13,
                                                    color: correcaoSobreposicoes.duplicadosAntes > 0 ? '#c94a4a' : '#505050',
                                                }}
                                            >
                                                {correcaoSobreposicoes.duplicadosAntes}
                                            </strong>
                                        </Inline>

                                        <Inline justifyContent='space-between'>
                                            <label style={{ fontSize: 12, color: '#7b7b7b' }}>Sobreposições:</label>
                                            <strong
                                                style={{
                                                    fontSize: 13,
                                                    color: correcaoSobreposicoes.kinksAntes > 0 ? '#c94a4a' : '#505050',
                                                }}
                                            >
                                                {correcaoSobreposicoes.kinksAntes}
                                            </strong>
                                        </Inline>

                                        <Inline justifyContent='space-between'>
                                            <label style={{ fontSize: 12, color: '#7b7b7b' }}>Após a correção:</label>
                                            <strong
                                                style={{
                                                    fontSize: 13,
                                                    color: correcaoSobreposicoes.kinksDepois === 0 ? '#2b8a3e' : '#c94a4a',
                                                }}
                                            >
                                                {correcaoSobreposicoes.kinksDepois} sobreposição(ões)
                                            </strong>
                                        </Inline>
                                    </Box>

                                    <Box gap={8}>
                                        <Inline justifyContent='space-between'>
                                            <label style={{ fontSize: 12, color: '#7b7b7b' }}>Área atual:</label>
                                            <strong style={{ fontSize: 13, color: '#505050' }}>
                                                {correcaoSobreposicoes.areaAntes.toFixed(2)} ha
                                            </strong>
                                        </Inline>

                                        <Inline justifyContent='space-between'>
                                            <label style={{ fontSize: 12, color: '#7b7b7b' }}>Área corrigida:</label>
                                            <strong style={{ fontSize: 13, color: '#2b8a3e' }}>
                                                {correcaoSobreposicoes.areaDepois.toFixed(2)} ha
                                            </strong>
                                        </Inline>

                                        <Inline justifyContent='space-between'>
                                            <label style={{ fontSize: 12, color: '#7b7b7b' }}>Variação da área:</label>
                                            <strong
                                                style={{
                                                    fontSize: 13,
                                                    color: correcaoSobreposicoes.arriscado ? '#c94a4a' : '#505050',
                                                }}
                                            >
                                                {correcaoSobreposicoes.variacaoArea.toFixed(2)}%
                                            </strong>
                                        </Inline>
                                    </Box>

                                    <Render condition={correcaoSobreposicoes.arriscado}>
                                        <Warning style={{ fontSize: 11 }} type='error'>
                                            A correção mudaria {correcaoSobreposicoes.variacaoArea.toFixed(1)}% da área — acima
                                            do limite seguro de {LIMITE_VARIACAO_AREA}%. Provavelmente o contorno tem um
                                            problema que precisa de ajuste manual.
                                        </Warning>
                                    </Render>

                                    <Render condition={!correcaoSobreposicoes.arriscado && !correcaoSobreposicoes.resolvido}>
                                        <Warning style={{ fontSize: 11 }} type='error'>
                                            Não foi possível resolver todas as sobreposições automaticamente. Restariam{' '}
                                            {correcaoSobreposicoes.kinksDepois} — arraste os vértices para separar as linhas
                                            sobrepostas.
                                        </Warning>
                                    </Render>

                                    <LegendaDaPreviaNoMapa rotuloVertices='Vértices alterados pela correção' />

                                    <ButtonCustom
                                        onClick={() => aplicarCorrecaoSobreposicoes()}
                                        icon={<FontAwesomeIcon icon={faCheck} />}
                                        disabled={!temAlgoParaCorrigir || correcaoSobreposicoes.arriscado}
                                        title={
                                            temAlgoParaCorrigir && !correcaoSobreposicoes.arriscado
                                                ? 'Aplicar a correção no contorno'
                                                : 'Correção automática indisponível para este contorno'
                                        }
                                        style={{ width: '100%', justifyContent: 'center' }}
                                    >
                                        Confirmar correção
                                    </ButtonCustom>

                                </div>
                            </ModalPopOver>

                            <ModalPopOver
                                title='Redutor de trilhas'
                                open={openReducaoTrilhas}
                                onClose={() => setOpenReducaoTrilhas(false)}
                                width={330}
                                height='auto'
                                top={30}
                                right={afastamentoPopOver}
                            >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                                    <Warning style={{ fontSize: 11 }} type='info'>
                                        Vértices que estiverem a menos da distância informada do vértice anterior serão
                                        removidos. Confira a prévia no mapa antes de confirmar.
                                    </Warning>

                                    <div>
                                        <label style={{ fontSize: 12, color: '#7b7b7b', fontWeight: 600 }}>
                                            Distância entre os vértices:{' '}
                                            <strong style={{ color: '#505050', fontSize: 13 }}>{distanciaTrilhas}m</strong>
                                        </label>
                                        <Slider
                                            min={1}
                                            max={50}
                                            step={1}
                                            value={distanciaTrilhas}
                                            onChange={(valor) => setDistanciaTrilhas(valor as number)}
                                            marks={{ 1: '1m', 25: '25m', 50: '50m' }}
                                        />
                                    </div>

                                    <ResumoDaPrevia previa={previaReducaoTrilhas} />

                                    <ButtonCustom
                                        onClick={() => aplicarReducaoTrilhas()}
                                        icon={<FontAwesomeIcon icon={faCheck} />}
                                        disabled={previaReducaoTrilhas.removidos <= 0}
                                        title={
                                            previaReducaoTrilhas.removidos > 0
                                                ? `${previaReducaoTrilhas.removidos} vértice(s) serão removidos`
                                                : 'Nenhum vértice seria removido com esse intervalo'
                                        }
                                        style={{ width: '100%', justifyContent: 'center' }}
                                    >
                                        Confirmar redução
                                    </ButtonCustom>

                                </div>
                            </ModalPopOver>

                            <ModalPopOver
                                title='Reduzir trilhas retas'
                                open={openReducaoRetas}
                                onClose={() => setOpenReducaoRetas(false)}
                                width={330}
                                height='auto'
                                top={30}
                                right={afastamentoPopOver}
                            >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                                    <Warning style={{ fontSize: 11 }} type='info'>
                                        Simplifica apenas os trechos retos: vértices que não alteram o formato da linha são
                                        removidos, preservando cantos e curvas. Confira a prévia no mapa antes de confirmar.
                                    </Warning>

                                    <div>
                                        <label style={{ fontSize: 12, color: '#7b7b7b', fontWeight: 600 }}>
                                            Tolerância:{' '}
                                            <strong style={{ color: '#505050', fontSize: 13 }}>{toleranciaRetas}m</strong>
                                        </label>
                                        <Slider
                                            min={0.5}
                                            max={5}
                                            step={0.5}
                                            value={toleranciaRetas}
                                            onChange={(valor) => setToleranciaRetas(valor as number)}
                                            marks={{ 0.5: '0,5m', 5: '5m' }}
                                        />
                                    </div>

                                    <ResumoDaPrevia previa={previaReducaoRetas} />

                                    <ButtonCustom
                                        onClick={() => aplicarReducaoRetas()}
                                        icon={<FontAwesomeIcon icon={faCheck} />}
                                        disabled={previaReducaoRetas.removidos <= 0}
                                        title={
                                            previaReducaoRetas.removidos > 0
                                                ? `${previaReducaoRetas.removidos} vértice(s) serão removidos`
                                                : 'Nenhum trecho reto seria simplificado com essa tolerância'
                                        }
                                        style={{ width: '100%', justifyContent: 'center' }}
                                    >
                                        Confirmar redução
                                    </ButtonCustom>

                                </div>
                            </ModalPopOver>

                        </WrapperMapa>
                    </ContainerMotion>
                </OverlayMotion>
            )}
        </AnimatePresence >
    );
};